import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';
import { personService } from '@shared/services/personService';
import { useEffect } from 'react';
import CustomDatePicker from '@shared/components/CustomDatePicker';

const SystemDocumentsForm = ({ onClose, onConfirm, editData = null, personId, state }) => {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);

    const docTypeOptions = [
        { value: 'type1', label: 'persons.systemDocuments.sickSheet' },
        { value: 'type2', label: 'persons.systemDocuments.disabilitySheet' },
        { value: 'type3', label: 'persons.systemDocuments.diploma' },
        { value: 'type4', label: 'persons.systemDocuments.certificate' },
        { value: 'type5', label: 'persons.systemDocuments.otherDocuments' }
    ];

    const validationSchema = yup.object({
        docdate: yup.string().required(t('validation.person.systemDocuments.docDateRequired')),
        empdoctype: yup.string().required(t('validation.person.systemDocuments.docTypeRequired')),
        specdate: yup.string().required(t('validation.person.systemDocuments.validUntilRequired')),
        description: yup.string().required(t('validation.person.systemDocuments.descriptionRequired')),
        note: yup.string().required(t('validation.person.systemDocuments.contentRequired'))
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            personId: personId,
            docdate: editData?.docdate || '',
            empdoctype: editData?.empdoctype || '',
            specdate: editData?.specdate || '',
            description: editData?.description || '',
            note: editData?.note || ''
        }
    });

    useEffect(() => {
        if (editData) {
            setValue('empdoctype', editData.empdoctype);
        }
    }, [editData, setValue]);

    const submitForm = async (data) => {
        if (editData) {
            await personService.editSystemDocument(editData.id, data);
        } else {
            await personService.createSystemDocument(data);
        }
        onConfirm(data);
        onClose();
    }

    const formatDateForApi = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return date;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const onFormSubmit = (data) => {
        const formattedData = {
            personId: personId,
            docdate: formatDateForApi(data.docdate),
            empdoctype: data.empdoctype,
            specdate: formatDateForApi(data.specdate),
            description: data.description,
            note: data.note
        };
        submitForm(formattedData);
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('persons.systemDocuments.edit') : t('persons.systemDocuments.add')}
                        </h4>
                        <form id="systemDocumentsForm" className="region-form" onSubmit={handleSubmit(onFormSubmit)}>
                            <div className="form-grid">
                                <div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.systemDocuments.docDate')} <span className="required">*</span>
                                        </span>
                                        <CustomDatePicker
                                            name="docdate"
                                            value={watch('docdate')}
                                            onChange={(date) => setValue('docdate', date, { shouldValidate: true })}
                                            errors={errors}
                                            placeholderText={t('common.dateFormat')}
                                            maxDate={new Date()}
                                            renderInPortal={true}
                                            popperPlacement="top-start"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.systemDocuments.docType')} <span className="required">*</span>
                                        </span>
                                        <select
                                            className={`form-control ${errors.empdoctype ? 'is-invalid' : ''}`}
                                            {...register('empdoctype')}
                                            defaultValue={state.selectedData?.empdoctype || ''}
                                        >
                                            <option value="">{t('common.select')}</option>
                                            {docTypeOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {t(option.label)}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.empdoctype && (
                                            <div className="invalid-feedback">
                                                {errors.empdoctype.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.systemDocuments.validUntil')} <span className="required">*</span>
                                        </span>
                                        <CustomDatePicker
                                            name="specdate"
                                            value={watch('specdate')}
                                            onChange={(date) => setValue('specdate', date, { shouldValidate: true })}
                                            errors={errors}
                                            placeholderText={t('common.dateFormat')}
                                            minDate={watch('docdate') || undefined}
                                            renderInPortal={true}
                                            popperPlacement="top-start"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.systemDocuments.description')} <span className="required">*</span>
                                        </span>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            {...register('description')}
                                        />
                                        {errors.description && (
                                            <div className="invalid-feedback">
                                                {errors.description.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t('persons.systemDocuments.content')} <span className="required">*</span>
                                        </span>
                                        <textarea
                                            className={`form-control ${errors.note ? 'is-invalid' : ''}`}
                                            {...register('note')}
                                        />
                                        {errors.note && (
                                            <div className="invalid-feedback">
                                                {errors.note.message}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="systemDocumentsForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="form-btn btn-red" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemDocumentsForm;
